/**
 * Security utilities for PIN-based app lock
 * Provides client-side validation and secure storage helpers
 */
import i18n from '../i18n';

/**
 * Validate PIN format (6 digits)
 */
export function validatePinFormat(pin: string): { valid: boolean; error?: string } {
  if (!pin || pin.length === 0) {
    return { valid: false, error: i18n.t('messages:pinIsRequired') };
  }

  if (pin.length !== 6) {
    return { valid: false, error: i18n.t('messages:pinMustBeExactly6Digits') };
  }

  if (!/^\d+$/.test(pin)) {
    return { valid: false, error: i18n.t('messages:pinMustContainOnlyDigits') };
  }

  return { valid: true };
}

/**
 * Check if PIN is too simple (sequential, repeated digits)
 */
export function checkPinStrength(pin: string): { strong: boolean; warning?: string } {
  // Check for repeated digits (1111, 2222, etc.)
  if (/^(\d)\1+$/.test(pin)) {
    return { strong: false, warning: i18n.t('messages:pinShouldNotHaveAllSameDigits') };
  }

  // Check for sequential ascending (1234, 2345, etc.)
  let isSequential = true;
  for (let i = 0; i < pin.length - 1; i++) {
    if (parseInt(pin[i]) + 1 !== parseInt(pin[i + 1])) {
      isSequential = false;
      break;
    }
  }
  if (isSequential) {
    return { strong: false, warning: i18n.t('messages:pinShouldNotBeSequential') };
  }

  // Check for sequential descending (4321, 5432, etc.)
  isSequential = true;
  for (let i = 0; i < pin.length - 1; i++) {
    if (parseInt(pin[i]) - 1 !== parseInt(pin[i + 1])) {
      isSequential = false;
      break;
    }
  }
  if (isSequential) {
    return { strong: false, warning: i18n.t('messages:pinShouldNotBeSequential') };
  }

  // Check for common patterns
  const commonPins = ['0000', '1234', '1111', '1212', '7777', '1004'];
  if (commonPins.includes(pin)) {
    return { strong: false, warning: i18n.t('messages:pinIsTooCommon') };
  }

  return { strong: true };
}

/**
 * Lock state storage keys
 */
const STORAGE_KEYS = {
  LOCK_ENABLED: 'app_lock_enabled',
  AUTO_LOCK_TIMEOUT: 'app_lock_timeout',
  LAST_ACTIVITY: 'app_last_activity',
  HAS_PIN: 'app_has_pin',
  /** Set while the screen is locked; cleared only by a verified PIN, a PIN reset, a fresh sign-in or logout. */
  LOCKED: 'app_locked',
  /** One-shot marker: a real sign-in just happened (not a page refresh restoring the session). */
  FRESH_AUTH: 'app_lock_fresh_auth',
} as const;

/** The key the lock flag lives under — exported so other tabs can react to it via the `storage` event. */
export const APP_LOCKED_STORAGE_KEY = STORAGE_KEYS.LOCKED;

/*
 * Why the lock itself is persisted (medopd-docs 134): the screen lock used to live only in React state,
 * and a page refresh was treated as a fresh login, so refreshing the browser removed the lock without
 * the PIN. The flag below survives a refresh (and is shared by the browser's tabs); AppLockContext reads
 * it synchronously on load, before any screen renders, and only clears it after the server has verified
 * the PIN (or a PIN reset / new sign-in / logout). Clearing site data still drops it — that also drops the
 * session cookie's companion state, and the server session itself remains the real authentication.
 */
export function setLockedFlag(): void {
  try { localStorage.setItem(STORAGE_KEYS.LOCKED, '1'); } catch { /* storage blocked: in-memory lock still applies */ }
}

export function clearLockedFlag(): void {
  try { localStorage.removeItem(STORAGE_KEYS.LOCKED); } catch { /* ignore */ }
}

export function isLockedFlagSet(): boolean {
  try { return localStorage.getItem(STORAGE_KEYS.LOCKED) === '1'; } catch { return false; }
}

/** Called by the sign-in flow (password / MFA) — NOT by the session restore that runs on every refresh. */
export function markFreshAuthentication(): void {
  try { sessionStorage.setItem(STORAGE_KEYS.FRESH_AUTH, String(Date.now())); } catch { /* ignore */ }
}

/**
 * True once after a real sign-in in this tab (within two minutes), then never again until the next
 * sign-in. A page refresh does not set it, so a refresh can no longer pass for a fresh login.
 */
export function consumeFreshAuthentication(): boolean {
  try {
    const at = Number(sessionStorage.getItem(STORAGE_KEYS.FRESH_AUTH) || 0);
    sessionStorage.removeItem(STORAGE_KEYS.FRESH_AUTH);
    return at > 0 && Date.now() - at < 2 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * Get lock state from localStorage
 */
export function getLockState() {
  try {
    return {
      isEnabled: localStorage.getItem(STORAGE_KEYS.LOCK_ENABLED) === 'true',
      autoLockTimeout: parseInt(localStorage.getItem(STORAGE_KEYS.AUTO_LOCK_TIMEOUT) || '86400', 10),
      lastActivity: parseInt(localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY) || '0', 10),
      hasPin: localStorage.getItem(STORAGE_KEYS.HAS_PIN) === 'true',
    };
  } catch (error) {
    console.error('Failed to get lock state:', error);
    return {
      isEnabled: false,
      autoLockTimeout: 86400,
      lastActivity: 0,
      hasPin: false,
    };
  }
}

/**
 * Save lock state to localStorage
 */
export function saveLockState(state: {
  isEnabled?: boolean;
  autoLockTimeout?: number;
  lastActivity?: number;
  hasPin?: boolean;
}) {
  try {
    if (state.isEnabled !== undefined) {
      localStorage.setItem(STORAGE_KEYS.LOCK_ENABLED, state.isEnabled.toString());
    }
    if (state.autoLockTimeout !== undefined) {
      localStorage.setItem(STORAGE_KEYS.AUTO_LOCK_TIMEOUT, state.autoLockTimeout.toString());
    }
    if (state.lastActivity !== undefined) {
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, state.lastActivity.toString());
    }
    if (state.hasPin !== undefined) {
      localStorage.setItem(STORAGE_KEYS.HAS_PIN, state.hasPin.toString());
    }
  } catch (error) {
    console.error('Failed to save lock state:', error);
  }
}

/**
 * Clear lock state from localStorage
 */
export function clearLockState() {
  try {
    localStorage.removeItem(STORAGE_KEYS.LOCK_ENABLED);
    localStorage.removeItem(STORAGE_KEYS.AUTO_LOCK_TIMEOUT);
    localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
    localStorage.removeItem(STORAGE_KEYS.HAS_PIN);
    localStorage.removeItem(STORAGE_KEYS.LOCKED);
  } catch (error) {
    console.error('Failed to clear lock state:', error);
  }
}

/**
 * Update last activity timestamp
 */
export function updateLastActivity() {
  saveLockState({ lastActivity: Date.now() });
}

/**
 * Check if app should be locked based on inactivity
 */
export function shouldLockApp(): boolean {
  const state = getLockState();
  
  if (!state.isEnabled || !state.hasPin) {
    return false;
  }

  const now = Date.now();
  const inactiveTime = (now - state.lastActivity) / 1000; // seconds
  
  return inactiveTime >= state.autoLockTimeout;
}

/**
 * Format timeout seconds to human-readable string
 */
export function formatTimeout(seconds: number): string {
  if (seconds < 60) {
    return i18n.t('messages:timeoutSeconds', { count: seconds });
  }

  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return i18n.t('messages:timeoutMinutes', { count: minutes });
  }

  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return i18n.t('messages:timeoutHours', { count: hours });
  }

  const days = Math.floor(seconds / 86400);
  return i18n.t('messages:timeoutDays', { count: days });
}
